import snakeCase from 'lodash/snakeCase';
import split from 'lodash/split';
import findIndex from 'lodash/findIndex';
import { combineReducers } from 'redux';
import { routerReducer } from 'react-router-redux';
import {
  combineForms,
} from 'react-redux-form';
import Moment from 'moment';

import Raven from './sentry';

import {
  AUTH_TOKEN,
  AUTH_TOKEN_USER,
  CURRENT_ACCOUNT,
  SIGN_UP_PERSONAL_INFO,
  SIGN_UP_INVESTMENT_INFO,
  SIGN_UP_PLAID_INFO,
  SIGN_UP_SIGNATURE,
  SIGN_UP_BACKUP_WITHHOLDING,
  SIGN_UP_COMPLETED,
  TRANSFER_FUNDS_INFO,
  TRANSFER_FUNDS_COMPLETED,
  LOGOUT,
  SERVICE_STATUS_CHECK_REQUESTED,
  SERVICE_STATUS_CHECK_SUCCEED,
  SERVICE_STATUS_CHECK_FAILED,
  TRADE_SECURITY_INFO,
  TRADE_SECURITY_TRANSFER_INFO,
  TRADE_SECURITY_COMPLETED,
  ACCOUNT_VALUE_COMPLETED,
  ACCOUNT_VALUE_REQUESTED,
  SET_ACCREDITATION,
  HISTORY_FILTER_TOGGLE,
  HISTORY_FILTER_RESET,
  TRANSFER_CANCELED,
} from './actions';
import {
  PLACE_ORDER,
  PLACE_ORDER_SUCCESSFUL,
  PLACE_ORDER_FAILURE,
  ORDER_CANCELED,
} from './actions/investment';
import {
  NOTIFICATION_ADD,
  NOTIFICATION_DISMISS,
  NOTIFICATION_PIN,
} from './actions/notifications';

import {
  ALERT_ADD,
  ALERT_DISMISS,
} from './actions/alerts';

import {
  initialPersonalInfoState,
  initialInvestmentInfoState,
  initialCredentialsState,
  initialLoginState,
  initialForgotPwdState,
  initialResetPwdState,
  initialTransferFundsState,
  initialOrderAmountState,
  initialOrderDisclosuresState,
  initialOrderPaymentState,
  initialExternalOrderAmountState,
  initialExternalOrderDisclosuresState,
  initialExternalOrderDeliveryState,
  initialExternalOrderPaymentState,
  initialManageAccountState,
  initialTradeOrderType,
  initialTradeSecurityState,
  initialChangeCrednetialsState,
  initialUploadVerifyDocsState,
  initialUploadDriverLicenseState,
  initialIndicateInterest,
  initialSuitabilityState,
} from './components/Forms/Schemas/baseRrf';

import {
  MANAGE_ACCOUNT_FORM_MODEL,
  OFFER_ORDER_FORM_MODEL,
  EXTERNAL_OFFER_ORDER_FORM_MODEL,
  SIGNUP_FORM_MODEL,
  LOGIN_FORM_MODEL,
  FORGOT_PWD_FORM_MODEL,
  RESET_PWD_FORM_MODEL,
  TRANSFER_FORM_MODEL,
  TRADE_SECURITY_FORM_MODEL,
  CHANGE_CREDENTIALS_FORM,
  UPLOAD_VERIFY_DOCS_FORM,
  UPLOAD_DRIVER_LICENSE_FORM,
  INDICATE_INTEREST_FORM_MODEL,
  SUITABILITY_FORM_MODEL,
} from './form_models';

import {
  BANK_LINK_DISCLAIMER,
  BANK_LINK_PLAID_INFO,
  BANK_LINK_SIGNATURE,
  BANK_LINK_COMPLETED,
} from './actions/bankLinks';

import {
  SHOW_MODAL,
  HIDE_MODAL,
} from './actions/modals';

import { CANCEL_INDICATION_SUCCESSFUL } from './actions/indicateInterest';
import { GET_CONFIGS, GET_CONFIGS_FAILURE, GET_CONFIGS_SUCCESSFUL } from './actions/configs';

/**
 * reducers
 */
function authToken(state = null, action) {
  switch (action.type) {
    case AUTH_TOKEN:
      return action.token || null;
    default:
      return state;
  }
}

function isAccredited(state = false, action) {
  switch (action.type) {
    case SET_ACCREDITATION:
      return action.isAccredited || false;
    default:
      return state;
  }
}


function authTokenUser(state = null, action) {
  switch (action.type) {
    case AUTH_TOKEN_USER:
      return action.user || null;
    default:
      return state;
  }
}


function currentAccount(state = '', action) {
  switch (action.type) {
    case CURRENT_ACCOUNT:
      return action.accountId;
    default:
      return state;
  }
}

// actions with these types will be called throughout the investment flow
// as user's complete various requirements in the flow
function investmentReducer(state = {}, action) {
  switch (action.type) {
    case PLACE_ORDER:
      return {
        ...state,
        [action.offerId]: {
          ...state[action.offerId],
          isSubmitting: true,
        },
      };

    case PLACE_ORDER_SUCCESSFUL:
      return {
        ...state,
        [action.offerId]: {
          ...state[action.offerId],
          isSubmitting: false,
          subscribed: Date.now(),
          confirmationId: action.confirmationId,
          error: null,
        },
      };

    case PLACE_ORDER_FAILURE:
      return {
        ...state,
        [action.offerId]: {
          ...state[action.offerId],
          isSubmitting: false,
          error: action.error,
        },
      };

    default:
      return state;
  }
}


function signUpInfo(state = {}, action) {
  switch (action.type) {
    case AUTH_TOKEN:
      if (action.token) {
        return state;
      }
      return {};
    case SIGN_UP_PERSONAL_INFO:
      return {
        ...state,
        personalInfo: action.personalInfo,
      };
    case SIGN_UP_INVESTMENT_INFO:
      return {
        ...state,
        investmentInfo: action.investmentInfo,
      };
    case SIGN_UP_PLAID_INFO:
      return {
        ...state,
        plaidInfo: action.plaidInfo,
      };
    case SIGN_UP_SIGNATURE:
      return {
        ...state,
        customerAgreementSignatureData: action.signatureImage,
        customerAgreementSignatureImageEncoding: action.signatureImageEncoding,
      };
    case SIGN_UP_BACKUP_WITHHOLDING:
      return {
        ...state,
        backupWithholding: action.backupWithholding,
      };
    case SIGN_UP_COMPLETED:
      return {};
    default:
      return state;
  }
}

function bankLinkInfo(state = {}, action) {
  switch (action.type) {
    case BANK_LINK_DISCLAIMER:
      return {
        ...state,
        bankLinkDisclaimerStatus: action.bankLinkDisclaimerStatus,
      };
    case BANK_LINK_PLAID_INFO:
      return {
        ...state,
        plaidInfo: action.plaidInfo,
        completePlaidInfo: {},
      };
    case BANK_LINK_SIGNATURE:
      return {
        ...state,
        customerAgreementSignatureData: action.signatureImage,
        customerAgreementSignatureImageEncoding: action.signatureImageEncoding,
      };
    case BANK_LINK_COMPLETED:
      return {
        completePlaidInfo: state.plaidInfo,
      };
    default:
      return state;
  }
}

function transferFundsInfo(state = {}, action) {
  switch (action.type) {
    case TRANSFER_FUNDS_INFO:
      return {
        ...state,
        transferInfo: action.transferInfo,
      };
    case TRANSFER_FUNDS_COMPLETED:
      return {
        ...state,
        transferInfo: {
          ...state.transferInfo,
          eftOid: action.eftOid,
          requestDate: action.requestDate,
        },
      };
    default:
      return state;
  }
}

const offeringDocumentsReducer = (state = {}, action) => {
  switch (action.type) {
    case 'OFFERING_DOCUMENTS/REQUESTED':
      return {
        ...state,
        isFetching: new Date(),
        error: null,
      };
    case 'OFFERING_DOCUMENTS/SUCCEED':
      return {
        ...state,
        data: {
          ...state.data,
          [action.payload.offerId]: {
            ...action.payload.fetchedAt,
            ...action.payload.data,
          },
        },
        isFetching: false,
      };
    case 'OFFERING_DOCUMENTS/FAILED':
      return {
        ...state,
        isFetching: false,
        error: action.payload.err,
      };
    default:
      return state;
  }
};


const createFetchReducer = (identifier, defaultData) => {
  const ACTION_TYPE = snakeCase(identifier).toUpperCase();
  return (state = { isFetching: false }, { type, payload }) => {
    switch (type) {
      case `${ACTION_TYPE}/REQUESTED`:
        return {
          ...state,
          isFetching: new Date(),
        };
      case `${ACTION_TYPE}/SUCCEED`:
        return {
          ...payload || { data: defaultData },
          isFetching: false,
        };
      case `${ACTION_TYPE}/FAILED`:
        return {
          ...state,
          isFetching: false,
        };
      default:
        return state;
    }
  };
};


const createFetchReducerForAccountData = (identifier, defaultData) => {
  const ACTION_TYPE = snakeCase(identifier).toUpperCase();
  return (state = { }, { type, payload = {} }) => {
    const { accountNumber, ...filteredPayload } = payload;
    switch (type) {
      case `${ACTION_TYPE}/REQUESTED`:
        return {
          ...state,
          [accountNumber]: {
            ...state[accountNumber],
            isFetching: new Date(),
          },
        };
      case `${ACTION_TYPE}/SUCCEED`:
        return {
          [accountNumber]: {
            ...filteredPayload || { data: defaultData },
            isFetching: false,
          },
        };
      case `${ACTION_TYPE}/FAILED`:
        return {
          ...state,
          [accountNumber]: {
            ...state[accountNumber],
            isFetching: false,
          },
        };
      default:
        return state;
    }
  };
};

const createFetchReducerForQuoteData = (identifier, defaultData) => {
  const ACTION_TYPE = snakeCase(identifier).toUpperCase();
  return (state = { isFetching: false }, { type, payload = {} }) => {
    const { symbols, ...filteredPayload } = payload;
    const allSymbols = split(symbols, ',');
    switch (type) {
      case `${ACTION_TYPE}/REQUESTED`:
        return {
          ...state,
          ...Object.assign({}, ...allSymbols.map(symbol => ({
            [symbol]: {
              ...state[symbol],
              isFetching: new Date(),
            },
          }))),
        };
      case `${ACTION_TYPE}/SUCCEED`:
        return {
          ...state,
          ...(filteredPayload.data ? filteredPayload.data : defaultData),
        };
      case `${ACTION_TYPE}/FAILED`:
        return {
          ...state,
          ...Object.assign({}, ...allSymbols.map(symbol => ({
            [symbol]: {
              ...state[symbol],
              isFetching: false,
            },
          }))),
        };
      default:
        return state;
    }
  };
};

const notifications = (state = [], action) => {
  switch (action.type) {
    case NOTIFICATION_ADD:
      return action.newNotification ? [
        ...state,
        action.newNotification,
      ] : [...state];
    case NOTIFICATION_DISMISS:
      return [
        ...state.map(notification =>
          ((notification.id === action.id) ? {
            ...notification,
            visible: false,
          } : notification)),
      ];
    case NOTIFICATION_PIN:
      return [
        ...state.map(notification =>
          ((notification.id === action.id) ? {
            ...notification,
            pinned: true,
          } : notification)),
      ];
    default:
      return state;
  }
};

const alerts = (state = [], action) => {
  switch (action.type) {
    case ALERT_ADD:
      return action.newAlert ? [
        ...state,
        action.newAlert,
      ] : [...state];
    case ALERT_DISMISS:
      return [
        ...state.map(alert =>
          ((alert.id === action.id) ? {
            ...alert,
            visible: false,
          } : alert)),
      ];
    default:
      return state;
  }
};

function tradeSecurityInfo(state = {}, action) {
  switch (action.type) {
    case TRADE_SECURITY_INFO:
      return {
        ...state,
        tradeInfo: action.tradeInfo,
      };
    case TRADE_SECURITY_TRANSFER_INFO:
      return {
        ...state,
        transferInfo: action.transferInfo,
      };
    case TRADE_SECURITY_COMPLETED:
      return {
        ...state,
        tradeInfo: {
          ...state.tradeInfo,
          orderId: action.orderId,
          orderDate: action.orderDate,
          requestDate: new Moment(),
        },
      };
    default:
      return state;
  }
}

function accountValue(state = {}, action) {
  switch (action.type) {
    case ACCOUNT_VALUE_REQUESTED:
      return {
        ...state,
        isFetching: new Date(),
      };
    case ACCOUNT_VALUE_COMPLETED:
      return {
        ...state,
        ...action.accountValue,
        isFetching: false,
      };
    default:
      return state;
  }
}

function modals(state = {}, action) {
  switch (action.type) {
    case SHOW_MODAL:
      return {
        ...state,
        modalType: action.modalType,
      };
    case HIDE_MODAL:
      return {
        ...state,
        modalType: null,
      };
    default:
      return state;
  }
}

const user = createFetchReducer('user', {});
const suitabilityInformation = createFetchReducer('suitabilityInformation', {});
const userDownloads = createFetchReducer('userDownloads', {});
const accounts = createFetchReducer('accounts', []);
const accountCashBalanceByAccount = createFetchReducerForAccountData('accountCashBalance', {});
const accountPerformanceRawDataByAccount = createFetchReducerForAccountData('accountPerformanceRawData', {});
const accountPerformanceSummaryByAccount = createFetchReducerForAccountData('accountPerformanceSummary', {});
const accountPositionsByAccount = createFetchReducerForAccountData('accountPositions', []);
const accountTransactionsByAccount = createFetchReducerForAccountData('accountTransactions', []);
const accountOrdersByAccount = createFetchReducerForAccountData('accountOrders', []);
const accountCashTransactionsByAccount = createFetchReducerForAccountData('accountCashTransactions', []);
const accountBankLinks = createFetchReducerForAccountData('accountBankLinks', []);
const accountRestrictionsByAccount = createFetchReducerForAccountData('accountRestrictions', []);
const accountLocalRestrictionsByAccount = createFetchReducerForAccountData('accountLocalRestrictions', []);
const accountDownloadsByAccount = createFetchReducerForAccountData('accountDownloads', {});
const instructions = createFetchReducer('instructions', {});
const quoteData = createFetchReducerForQuoteData('quotedata', []);
const quoteChartData = createFetchReducerForQuoteData('results', []);
const enhancedQuoteData = createFetchReducerForQuoteData('enhancedquotedata', []);
const offerings = createFetchReducer('offerings', []);
const offeringSubscriptionsByAccount = createFetchReducerForAccountData('offeringSubscriptions', []);
const offeringOrdersByAccount = createFetchReducerForAccountData('offerOrders', []);
const userAccreditationStatus = createFetchReducer('userAccreditationStatus', {});
const manageAccountForm = combineForms({
  accountInfo: initialManageAccountState,
}, MANAGE_ACCOUNT_FORM_MODEL);
const indicateInterestForm = combineForms({ indicateInterest: initialIndicateInterest }, INDICATE_INTEREST_FORM_MODEL);
const rrSignupForms = combineForms({
  personalInfo: initialPersonalInfoState,
  investmentInfo: initialInvestmentInfoState,
  credentials: initialCredentialsState,
}, SIGNUP_FORM_MODEL);
const loginForm = combineForms(
  initialLoginState,
  LOGIN_FORM_MODEL,
);
const forgotPwdForm = combineForms(
  initialForgotPwdState,
  FORGOT_PWD_FORM_MODEL,
);
const resetPwdForm = combineForms(
  initialResetPwdState,
  RESET_PWD_FORM_MODEL,
);
const transferFundsForm = combineForms({
  transferFunds: initialTransferFundsState,
}, TRANSFER_FORM_MODEL);
const offerOrderForm = combineForms({
  offerId: '',
  offerOrderAmount: initialOrderAmountState,
  offerOrderDisclosures: initialOrderDisclosuresState,
  offerOrderTransferFunds: initialOrderPaymentState,
}, OFFER_ORDER_FORM_MODEL);
const externalOfferOrderForm = combineForms({
  offerId: '',
  externalOfferOrderAmount: initialExternalOrderAmountState,
  externalOfferOrderDisclosures: initialExternalOrderDisclosuresState,
  externalOfferOrderDelivery: initialExternalOrderDeliveryState,
  externalOfferOrderTransferFunds: initialExternalOrderPaymentState,
}, EXTERNAL_OFFER_ORDER_FORM_MODEL);
const tradeSecurityForm = combineForms({
  tradeOrderType: initialTradeOrderType,
  tradeSecurity: initialTradeSecurityState,
  tradeSecurityTransferFunds: initialOrderPaymentState,
}, TRADE_SECURITY_FORM_MODEL);

const changeCredentialsForm = combineForms({
  changeCredentials: initialChangeCrednetialsState,
}, CHANGE_CREDENTIALS_FORM);

const uploadVerifyDocsForm = combineForms({
  verifyDocs: initialUploadVerifyDocsState,
}, UPLOAD_VERIFY_DOCS_FORM);

const uploadDriverLicenseForm = combineForms({
  driverLicense: initialUploadDriverLicenseState,
}, UPLOAD_DRIVER_LICENSE_FORM);
const suitabilityForm = combineForms({
  suitabilityInformation: initialSuitabilityState,
}, SUITABILITY_FORM_MODEL);
const serviceStatus = (state = {}, action) => {
  switch (action.type) {
    case SERVICE_STATUS_CHECK_REQUESTED:
      return {
        isChecking: true,
        ...state,
        ...action.payload,
      };
    case SERVICE_STATUS_CHECK_SUCCEED:
      return {
        isChecking: false,
        errorCount: 0,
        ...action.payload,
      };
    case SERVICE_STATUS_CHECK_FAILED: {
      const errorCount = (state.errorCount || 0) + 1;
      return {
        isChecking: false,
        errorCount,
        ...action.payload,
      };
    }
    default:
      return state;
  }
};

const historyFilter = (state = { selected: [] }, action) => {
  switch (action.type) {
    case HISTORY_FILTER_TOGGLE: {
      const selectionCode = action.payload.code;
      const selectedIndex = findIndex(state.selected, item => item === selectionCode);
      let selected = [...state.selected];
      if (selectedIndex === -1) {
        selected = [
          ...state.selected,
          selectionCode,
        ];
      } else {
        selected.splice(selectedIndex, 1);
      }
      return {
        filteredAt: new Date(),
        selected,
      };
    }
    case HISTORY_FILTER_RESET: {
      return {
        filteredAt: new Date(),
        selected: [],
      };
    }
    default:
      return state;
  }
};

const cancelations = (state = {
  latestCanceledOrderID: null,
  latestCanceledTransferID: null,
  canceledTransfers: [],
  canceledOrders: [],
  canceledOfferOrders: [],
}, action) => {
  switch (action.type) {
    case TRANSFER_CANCELED: {
      return {
        ...state,
        latestCanceledTransferID: action.id,
        canceledTransfers: [
          ...state.canceledTransfers,
          action.id,
        ],
      };
    }
    case ORDER_CANCELED: {
      const cancelObj = {};
      if (action.orderType === 'ot') {
        cancelObj.canceledOfferOrders = [
          ...state.canceledOfferOrders,
          action.id,
        ];
      } else if (action.orderType === 'st') {
        cancelObj.canceledOrders = [
          ...state.canceledOrders,
          action.id,
        ];
      }
      return {
        ...state,
        ...cancelObj,
        latestCanceledOrderID: action.id,
      };
    }
    default:
      return state;
  }
};

const queryString = (state = {}, action) => {
  switch (action.type) {
    case 'QUERY_STRING':
      return {
        ...action.payload,
      };
    default:
      return state;
  }
};

const offeringWatches = (state = {}, action) => {
  switch (action.type) {
    case 'OFFERING_WATCHES/REQUESTED':
      return {
        ...state,
        isFetching: new Date(),
        error: null,
      };
    case 'OFFERING_WATCHES/SUCCEED':
      return {
        ...state,
        data: action.payload.data,
        isFetching: false,
      };
    case 'OFFERING_WATCHES/FAILED':
      return {
        ...state,
        isFetching: false,
        error: action.payload.err,
      };
    case CANCEL_INDICATION_SUCCESSFUL: {
      const { data } = state;
      if (data) {
        const idx = findIndex(state.data, { offer: { id: action.offerId } });
        data.splice(idx, 1);
      }
      return {
        isFetching: false,
        data,
      };
    }
    default:
      return state;
  }
};

const configs = (state = {}, action) => {
  switch (action.type) {
    case GET_CONFIGS:
      return {
        ...state,
        isFetching: new Date(),
        error: null,
      };
    case GET_CONFIGS_SUCCESSFUL:
      return {
        ...state,
        data: action.payload,
        isFetching: false,
      };
    case GET_CONFIGS_FAILURE:
      return {
        ...state,
        isFetching: false,
        error: action.payload,
      };
    default:
      return state;
  }
};

// reducers with a name ending in "Form" are automatically added
//  to the redux-persist blacklist
export const reducers = {
  accountBankLinks,
  accountCashBalanceByAccount,
  accountCashTransactionsByAccount,
  accountDownloadsByAccount,
  accountOrdersByAccount,
  accountPerformanceRawDataByAccount,
  accountPerformanceSummaryByAccount,
  accountPositionsByAccount,
  accountRestrictionsByAccount,
  accountLocalRestrictionsByAccount,
  accounts,
  accountTransactionsByAccount,
  accountValue,
  alerts,
  authToken,
  authTokenUser,
  bankLinkInfo,
  changeCredentialsForm,
  currentAccount,
  enhancedQuoteData,
  forgotPwdForm,
  instructions,
  investmentsByOffer: investmentReducer,
  loginForm,
  manageAccountForm,
  notifications,
  offerings,
  offeringDocumentsByOffer: offeringDocumentsReducer,
  offeringSubscriptionsByAccount,
  offeringOrdersByAccount,
  offerOrderForm,
  externalOfferOrderForm,
  quoteChartData,
  quoteData,
  queryString,
  resetPwdForm,
  router: routerReducer,
  rrSignupForms,
  indicateInterestForm,
  serviceStatus,
  signUpInfo,
  tradeSecurityForm,
  tradeSecurityInfo,
  transferFundsForm,
  transferFundsInfo,
  uploadVerifyDocsForm,
  user,
  suitabilityInformation,
  userAccreditationStatus,
  userDownloads,
  uploadDriverLicenseForm,
  isAccredited,
  historyFilter,
  cancelations,
  modals,
  offeringWatches,
  suitabilityForm,
  configs,
};


const combinedReducers = combineReducers(reducers);

export default (state, action) => {
  if (Raven && [AUTH_TOKEN_USER, LOGOUT, 'persist/REHYDRATE'].includes(action.type)) {
    Raven.setUserContext((action.payload && action.payload.authTokenUser) || action.user);
  }
  switch (action.type) {
    case LOGOUT:
      return combinedReducers(undefined, action);
    default:
      return combinedReducers(state, action);
  }
};



// WEBPACK FOOTER //
// ./src/reducers.js